import { LOB_IDS } from "@/shared/common/static";
import { LOCALISATION_CONFIG } from "@/shared/config/localisation";
import type { SharedPersonalDetailsState } from "@/shared/redux/reducers/personalDetailsSlice";
import { getAuthSessionIdentifiers } from "@/shared/utils/authSession";
import { addYears, format, parseISO } from "date-fns";
import { defaultHealthQuickQuestionsFormValues } from "../redux/health-quote/healthQuoteSlice";
import type {
  HealthAdditionalInfoValues,
  HealthDetailsFormValues,
  HealthHabitsFormValues,
  HealthPaymentValues,
  HealthPersonalFormValues,
  HealthQuickQuestionsFormValues,
  HealthUploadedDocumentMap,
} from "../redux/type";

function buildHealthCoverEndDate(startDate: string, policy_tenure: string) {
  if (!startDate || !policy_tenure) return null;

  const parsedStartDate = parseISO(startDate);
  const years = Number(policy_tenure);

  if (Number.isNaN(parsedStartDate.getTime()) || !Number.isInteger(years) || years < 1) {
    return null;
  }

  return format(addYears(parsedStartDate, years), "yyyy-MM-dd");
}

function normalizeNullableNumber(value: string | number | null | undefined) {
  const normalizedValue = value === null || value === undefined ? null : Number(value);

  return normalizedValue === null || Number.isNaN(normalizedValue) ? null : normalizedValue;
}

function getPaymentResponsePayload(paymentResponse: any) {
  return paymentResponse?.data && typeof paymentResponse.data === "object"
    ? paymentResponse.data
    : paymentResponse;
}

function buildHealthPlanName(selectedQuote: any) {
  const insurerPrefix = selectedQuote?.insurer?.name
    ?.replace(/insurance|insurer|pjsc|limited|co/gi, "")
    .trim();

  return insurerPrefix
    ? `${insurerPrefix}-${selectedQuote?.plan?.name}`
    : selectedQuote?.plan?.name;
}

function toHealthAttachType(fieldKey: string) {
  return fieldKey
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .split("_")
    .filter(Boolean)
    .map((word) => word.toUpperCase())
    .join("_");
}

function buildHealthUploadDocAttachmentsPayload(uploadedDocuments: HealthUploadedDocumentMap) {
  return Object.entries(uploadedDocuments || {}).reduce<Record<string, unknown>[]>(
    (attachments, [fieldKey, document]) => {
      if (!document) {
        return attachments;
      }

      attachments.push({
        docName: document.fileName || document.filename || "uploaded_document",
        docType: document.mimeType || "image/jpeg",
        file:
          (document as any).file || (document as any).base64 || (document as any).fileData || "",
        attachType: toHealthAttachType(fieldKey),
      });

      return attachments;
    },
    []
  );
}

function splitHealthMemberName(name: string) {
  const [firstName, ...restName] = name.trim().split(/\s+/).filter(Boolean);

  return {
    firstName: firstName || "",
    lastName: restName.join(" "),
  };
}

function normalizeDateOfBirth(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const normalized = value.trim();
  if (!normalized) {
    return null;
  }

  return /^\d{4}-\d{2}-\d{2}$/.test(normalized) ? normalized : null;
}

function buildHealthContactPayload(personalDetails: SharedPersonalDetailsState) {
  return {
    mobileCountryCode: `+${personalDetails.mobileNumber.countryCode.replace(/\D/g, "")}`,
    mobileNumber: personalDetails.mobileNumber.number || "",
    email: personalDetails.email || "",
  };
}

function buildHealthTravellersMoreInfo(params: {
  personalDetails: SharedPersonalDetailsState;
  healthDetails: HealthDetailsFormValues;
  additionalInfo: HealthAdditionalInfoValues;
}) {
  const { personalDetails, healthDetails, additionalInfo } = params;
  const members = healthDetails.members?.length
    ? healthDetails.members
    : [
        {
          name: `${personalDetails.firstName || ""} ${personalDetails.lastName || ""}`.trim(),
          dob: "",
          relationship: "self" as const,
          preExistingCondition: false,
        },
      ];

  return members.map((member) => {
    const { firstName, lastName } = splitHealthMemberName(member.name || "");

    return {
      first_name: firstName,
      last_name: lastName,
      date_of_birth: normalizeDateOfBirth(member.dob),
      gender: additionalInfo.gender || "",
      nationality: additionalInfo.nationality || "",
      cityOfResidence: additionalInfo.cityOfResidence || "",
      address: additionalInfo.address || "",
      emiratesId: additionalInfo.emiratesId || "",
      passportNo: additionalInfo.passportNumber || "",
      beneficiaryName: additionalInfo.beneficiaryName || "",
      beneficiaryRelationship: additionalInfo.beneficiaryRelation || "",
      sourceOfFunds: additionalInfo.sourceOfFund || "",
      isUaeResidentConfirmed: additionalInfo.uaeResidentConfirm,
    };
  });
}

function buildHealthBindIdentityPayload(additionalInfo: HealthAdditionalInfoValues) {
  const identityInfo = additionalInfo as any;

  return {
    emiratesId: additionalInfo.emiratesId || "",
    passportNo: additionalInfo.passportNumber || "",
    emiratesIdIssueDate: identityInfo.emiratesIdIssueDate || "",
    emiratesIdExpiryDate: identityInfo.emiratesIdExpiryDate || "",
    passportIssueDate: identityInfo.passportIssueDate || "",
    passportExpiryDate: identityInfo.passportExpiryDate || "",
  };
}

function buildHealthBindQuotePayload(params: {
  selectedQuote: any;
  clientId: string | number | null;
  sessionId: string | number | null;
  personalDetails: SharedPersonalDetailsState;
  healthDetails: HealthDetailsFormValues;
  additionalInfo: HealthAdditionalInfoValues;
  action: "BUY" | "NEXT_BUTTON" | "UPLOAD_DOC";
  attachments?: Record<string, unknown>[];
  lob_id?: number;
}) {
  const {
    selectedQuote,
    clientId,
    sessionId,
    personalDetails,
    healthDetails,
    additionalInfo,
    action,
    attachments = [],
    lob_id,
  } = params;

  return {
    client_id: normalizeNullableNumber(clientId),
    session_id: normalizeNullableNumber(sessionId),
    lob_id: lob_id ?? LOB_IDS.HEALTH,
    plan_name: buildHealthPlanName(selectedQuote),
    insurerId: String(selectedQuote?.insurer?.insurer_id ?? ""),
    action,
    insurerPlan: {
      id: String(selectedQuote?.plan?.id ?? ""),
      planName: selectedQuote?.plan?.name || "",
    },
    contact: buildHealthContactPayload(personalDetails),
    travellers_more_info: buildHealthTravellersMoreInfo({
      personalDetails,
      healthDetails,
      additionalInfo,
    }),
    identity: buildHealthBindIdentityPayload(additionalInfo),
    declarations: {
      isUaeResidentConfirmed: additionalInfo.uaeResidentConfirm,
    },
    attachments,
  };
}

export function buildHealthPersonalDetailsPayload(personalDetails: SharedPersonalDetailsState) {
  return {
    first_name: personalDetails.firstName || "",
    last_name: personalDetails.lastName || "",
    mobile_country_code: `+${personalDetails.mobileNumber.countryCode.replace(/\D/g, "")}`,
    mobile_number: personalDetails.mobileNumber.number || "",
    email: personalDetails.email || "",
    partner_code: personalDetails.partnerCode || "",
    friends_and_family_contact: personalDetails.friendsAndFamilyContact || "",
    marketing_consent: personalDetails.marketingConsent || "no",
  } as const;
}

export function buildHealthCreateSessionPayload(personalDetails: HealthPersonalFormValues) {
  return {
    assigned_user_id: null,
    payload: {
      first_name: personalDetails.firstName || "",
      last_name: personalDetails.lastName || "",
      country_code: personalDetails.mobileNumber.countryCode.replace(/\D/g, ""),
      mobile_number: personalDetails.mobileNumber.number || "",
      email: personalDetails.email || "",
      partner_code: personalDetails.partnerCode || "",
      friends_and_family_contact: personalDetails.friendsAndFamilyContact || "",
      marketing_consent: personalDetails.marketingConsent || "no",
    },
    lob_id: LOB_IDS.HEALTH,
  };
}

export function buildHealthDetailsPayload(healthDetails: HealthDetailsFormValues) {
  const policyEndDate = buildHealthCoverEndDate(
    healthDetails.startDate,
    healthDetails.policy_tenure
  );

  return {
    policy_type: healthDetails.policyType,
    coverage_amount: Number(healthDetails.coverageAmount),
    coverage_type: healthDetails.coverageType,
    policy_tenure: Number(healthDetails.policy_tenure),

    policy_dates: {
      start_date: healthDetails.startDate,
      end_date: policyEndDate,
    },

    existing_policy_details: {
      current_insurer: healthDetails.currentInsurer || "",
      policy_number: "",
      policy_expiry_date: healthDetails.policyExpiryDate || "",
    },

    members: healthDetails.members.map((member) => {
      const [firstName = "", ...rest] = member.name.split(" ");

      return {
        first_name: firstName,
        last_name: rest.join(" "),
        relationship: member.relationship,
        date_of_birth: member.dob,
      };
    }),
  };
}

function buildHealthHabitsPayload(healthHabits: HealthHabitsFormValues) {
  const selectedHabits = healthHabits.selectedHabits || [];

  return {
    non_smoker: selectedHabits.includes("non_smoker"),
    regular_exercise: selectedHabits.includes("regular_exercise"),
    healthy_diet: selectedHabits.includes("healthy_diet"),
    occasional_alcohol: selectedHabits.includes("occasional_alcohol"),
    no_alcohol: selectedHabits.includes("no_alcohol"),
    stress_management: selectedHabits.includes("stress_management"),
    adequate_sleep: selectedHabits.includes("adequate_sleep"),
    no_recreational_drugs: selectedHabits.includes("no_recreational_drugs"),
    maintains_healthy_weight: selectedHabits.includes("maintains_healthy_weight"),
    regular_health_checkup: selectedHabits.includes("regular_health_checkup"),
    vegetarian_vegan: selectedHabits.includes("vegetarian_vegan"),
    other: selectedHabits.includes("other") ? "Yes" : "",
  };
}

function buildQuickQuestionsPayload(
  healthHabits: HealthHabitsFormValues,
  quickQuestions: HealthQuickQuestionsFormValues
) {
  return {
    pre_existing_medical_condition: {
      answer: healthHabits.preExistingMedicalConditions,
      details: "",
    },
    hospitalized_last_2_years: {
      answer: healthHabits.hospitalizedLast2Years,
      details: "",
    },
    currently_on_medication: {
      answer: healthHabits.currentlyOnMedication,
      details: "",
    },
    surgery_last_5_years: {
      answer: healthHabits.surgeryLast5Years,
      details: "",
    },
    allergies: {
      answer: healthHabits.allergies,
      details: "",
    },
    tobacco_or_nicotine_use: {
      answer: healthHabits.tobaccoOrNicotine,
      details: "",
    },
    family_history_major_illness: {
      answer: healthHabits.familyHistoryMajorIllness,
      details: "",
    },
    other_medical_condition: {
      answer: healthHabits.otherMedicalCondition,
      details: quickQuestions.otherMedicalConditionDetails || "",
    },
  };
}

function buildLifestyleDetailsPayload(quickQuestions: HealthQuickQuestionsFormValues) {
  return {
    height_cm: Number(quickQuestions.heightCm || 0),
    weight_kg: Number(quickQuestions.weightKg || 0),
    bmi: Number(quickQuestions.bmi || 0),
    occupation_type: quickQuestions.occupationType,
    work_profile: quickQuestions.workProfile,
    annual_income_range: quickQuestions.annualIncomeRange,
    additional_information: quickQuestions.additionalInformation || "",
  };
}

function buildHealthQuickQuestionsPayload(quickQuestions: HealthQuickQuestionsFormValues) {
  const resolvedQuickQuestions = {
    ...defaultHealthQuickQuestionsFormValues,
    ...quickQuestions,
  };

  return {
    height_cm: resolvedQuickQuestions.heightCm,
    weight_kg: resolvedQuickQuestions.weightKg,
    bmi: resolvedQuickQuestions.bmi,
    medical_conditions: resolvedQuickQuestions.medicalConditions,
    occupation_type: resolvedQuickQuestions.occupationType,
    work_profile: resolvedQuickQuestions.workProfile,
    annual_income_range: resolvedQuickQuestions.annualIncomeRange,
    additional_information: resolvedQuickQuestions.additionalInformation,
    other_medical_condition_details: resolvedQuickQuestions.otherMedicalConditionDetails,
  };
}

export function buildHealthQuoteRequestBody(
  personalDetails: SharedPersonalDetailsState,
  healthDetails: HealthDetailsFormValues,
  healthHabits: HealthHabitsFormValues,
  quickQuestions: HealthQuickQuestionsFormValues,
  authSession: any
) {
  const { clientId, sessionId } = getAuthSessionIdentifiers(authSession);
  const healthDetailsPayload = buildHealthDetailsPayload(healthDetails);
  const healthHabitsPayload = buildHealthHabitsPayload(healthHabits);

  return {
    personal_details: {
      ...buildHealthPersonalDetailsPayload(personalDetails),
      nationality: "",
    },
    health_details: healthDetailsPayload,
    health_habits: healthHabitsPayload,
    quick_questions: buildQuickQuestionsPayload(healthHabits, quickQuestions),
    medical_conditions: quickQuestions.medicalConditions,
    lifestyle_details: buildLifestyleDetailsPayload(quickQuestions),
    client_id: clientId ? Number(clientId) : null,
    session_id: sessionId ? Number(sessionId) : null,
    lob_id: LOB_IDS.HEALTH,
  };
}

export function buildHealthBindPayload(params: {
  selectedQuote: any;
  clientId: string | number | null;
  sessionId: string | number | null;
  personalDetails: SharedPersonalDetailsState;
  healthDetails: HealthDetailsFormValues;
  additionalInfo: HealthAdditionalInfoValues;
  lob_id?: number;
}) {
  return buildHealthBindQuotePayload({
    ...params,
    action: "NEXT_BUTTON",
  });
}

export function buildHealthBuyNowBindPayload(params: {
  selectedQuote: any;
  clientId: string | number | null;
  sessionId: string | number | null;
  personalDetails: SharedPersonalDetailsState;
  healthDetails: HealthDetailsFormValues;
  additionalInfo: HealthAdditionalInfoValues;
  lob_id?: number;
}) {
  return buildHealthBindQuotePayload({
    ...params,
    action: "BUY",
  });
}

export function buildHealthUploadDocPayload(params: {
  selectedQuote: any;
  clientId: string | number | null;
  sessionId: string | number | null;
  personalDetails: SharedPersonalDetailsState;
  healthDetails: HealthDetailsFormValues;
  additionalInfo: HealthAdditionalInfoValues;
  documents: HealthUploadedDocumentMap;
  lob_id?: number;
}) {
  const { documents, ...bindQuoteParams } = params;

  return buildHealthBindQuotePayload({
    ...bindQuoteParams,
    action: "UPLOAD_DOC",
    attachments: buildHealthUploadDocAttachmentsPayload(documents),
  });
}

export function buildHealthSelectedQuotePayload(quote: any) {
  const commission = quote?.plan?.commission || quote?.commission;
  const sections = (Array.isArray(quote?.sections) ? quote.sections : []) as Array<{
    title?: string;
    items?: Array<{ is_checked?: boolean; label?: string }>;
  }>;

  return {
    insurer_name: quote?.insurer?.name || "",
    insurer_id: quote?.insurer?.insurer_id || quote?.insurer?.id || null,
    insurer_logo: quote?.insurer?.insurer_logo || null,
    plan_name: quote?.plan?.name || "",
    plan_id: quote?.plan?.id || null,
    premium: quote?.plan?.price?.amount ?? null,
    vat: quote?.plan?.price?.vat ?? null,
    total_premium: quote?.plan?.price?.amount_with_vat ?? null,
    currency: quote?.plan?.price?.currency || null,
    sections,
    selected_addOns:
      sections
        .find((section) => section.title === "Add-Ons")
        ?.items?.filter((item) => item.is_checked === true)
        .map((item) => item.label) || [],
    ...(commission
      ? {
          commission: {
            percentage: commission?.percentage ?? null,
            amount: commission?.amount ?? null,
            currency: commission?.currency || null,
          },
        }
      : {}),
  };
}

export function buildHealthAttachmentsPayload(
  uploadedDocuments: HealthUploadedDocumentMap
): Record<string, unknown>[] {
  return Object.entries(uploadedDocuments).reduce<Record<string, unknown>[]>(
    (attachments, [fieldKey, document]) => {
      if (!document) return attachments;

      attachments.push({
        fieldKey,
        docName: document.fileName || document.filename,
        docType: document.mimeType || "",
        doc_url:
          (document.uploadResponse as { payload?: { final_filename?: string } } | undefined)
            ?.payload?.final_filename || "",
        size: document.size || 0,
        uploadedAt: document.uploadedAt,
      });

      return attachments;
    },
    []
  );
}

export function buildHealthPaymentPayload(params: {
  selectedQuote: any;
  clientId: string | number | null;
  sessionId: string | number | null;
  paymentData: HealthPaymentValues;
}) {
  const { selectedQuote, clientId, sessionId, paymentData } = params;
  const amountWithVat =
    Number(selectedQuote?.plan?.price?.amount_with_vat) ||
    Number(selectedQuote?.plan?.price?.amount) ||
    0;

  return {
    client_id: clientId ? Number(clientId) : null,
    quote_id: sessionId ? Number(sessionId) : 0,
    currency: String(
      selectedQuote?.plan?.price?.currency || LOCALISATION_CONFIG.currencyCode
    ).toLowerCase(),
    payment_method: "card",
    amount: amountWithVat,
    plan_name: selectedQuote?.plan?.name || "",
    provider:
      selectedQuote?.insurer?.name && selectedQuote?.plan?.name
        ? `${selectedQuote.insurer.name} - ${selectedQuote.plan.name}`
        : selectedQuote?.insurer?.name || "Unknown",
    card_number: paymentData.cardNumber.replace(/\s/g, ""),
    card_expiry: paymentData.expiry,
    card_cvv: paymentData.cvv,
    card_name: paymentData.cardHolder,
  };
}

export function buildHealthIssuePolicyPayload(params: {
  selectedQuote: any;
  clientId: string | number | null;
  sessionId: string | number | null;
  paymentResponse: any;
  healthDetails: Pick<HealthDetailsFormValues, "startDate" | "policy_tenure">;
  lobId?: number;
}) {
  const { selectedQuote, clientId, sessionId, paymentResponse, healthDetails, lobId } = params;
  const paymentPayload = getPaymentResponsePayload(paymentResponse);
  const policyEndDate = buildHealthCoverEndDate(
    healthDetails.startDate,
    healthDetails.policy_tenure
  );

  return {
    insurerId: Number(selectedQuote?.insurer?.insurer_id ?? 0) || null,
    action: "ISSUE_POLICY",
    client_id: clientId ? Number(clientId) : null,
    session_id: sessionId ? Number(sessionId) : null,
    policy_issued_by: "retail",
    plan_name: paymentPayload?.plan_name || selectedQuote?.plan?.name || "",
    start_date: healthDetails.startDate || null,
    end_date: policyEndDate,
    provider_transaction_id:
      paymentPayload?.provider_transaction_id || paymentPayload?.transaction_id || "",
    lob_id: lobId ?? LOB_IDS.HEALTH,
  };
}
